/* eslint-disable no-unused-vars */
import type Vue from 'vue';
import { Toast, Dialog } from '@atom/atom-ui';
import '@atom/atom-ui/es_750_rem/dialog/style';
import '@atom/atom-ui/es_750_rem/button/style';
import '@atom/atom-ui/es_750_rem/toast/style';
import { isInSjcg, registerWebListener } from '@king-fisher/mobileweb-lib';
import { getNotificationStatus, jumpNotificationSetting, needConfirmPush } from './utils';
import { fetchSubscriptionStatus, updateSubscriptionStatus, setPushSwitch } from './apis';
import {
  SUBSCRIBE_FAILED_MSG,
  UN_SUBSCRIBE_FAILED_MSG,
  SUBSCRIBE_TYPE,
  CANCEL_SUBSCRIBE_SUC_MSG,
} from './constants';
import { statClick } from '@/config/stat';

/**
 * 订阅功能 Mixin 类型定义
 * 用于组件继承订阅/取消订阅功能
 */
export interface SubscribeMixinType extends Vue {
  // props
  /** 是否可订阅 */
  subscribable: boolean;
  /** 订阅栏目ID */
  columnId: number;
  /** 业务名称文案 */
  businessName: string;
  /** 需要二次确认推送的厂商列表 */
  needConfirmPushDevice: string[];

  // data
  /** 订阅接口请求状态 */
  loading: boolean;
  /** 是否开启推送 */
  isPushOpen: boolean;
  /** 是否已订阅 */
  isSubscribed: boolean;
  /** 系统推送开关是否开启 */
  isSystemPushOpen: boolean;
  /** 是否需要二次确认推送 */
  isNeedConfirmPush: boolean;

  // methods
  /** 获取订阅状态 */
  fetchSubscriptionStatus(): Promise<void>;
  /** 切换订阅状态 */
  toggleSubscription(): Promise<void>;
  /** 打开功能推送开关 */
  setPushSwitch(action: 'open' | 'close'): Promise<void>;
  /** 显示推送确认弹窗 */
  showConfirmPushDialog(): void;
  /** 显示订阅成功提示 */
  showSuccessDialog(): void;
}

/**
 * 订阅功能 Mixin
 * 提供订阅/取消订阅相关的 props、data 和方法
 */
const subscribeMixin = {
  props: {
    // 是否可订阅，端外或者非登录时，需要设置false
    subscribable: {
      type: Boolean,
      default: isInSjcg(),
    },
    // 订阅栏目的ID
    columnId: {
      type: Number,
      default: '37',
    },
    // 业务名称文案
    businessName: {
      type: String,
      default: '研报机会',
    },
    // 需要二次确认推送的厂商列表
    needConfirmPushDevice: {
      type: Array,
      default: () => ['HUAWEI', 'OPPO'],
    },
  },
  data() {
    return {
      loading: false,
      isPushOpen: false,
      isSubscribed: false,
      isNeedConfirmPush: false,
      isSystemPushOpen: false,
    };
  },
  created(this: SubscribeMixinType) {
    this.fetchSubscriptionStatus();
    registerWebListener('onShow', () => {
      this.fetchSubscriptionStatus();
    });
  },
  methods: {
    // 获取订阅状态
    async fetchSubscriptionStatus(this: SubscribeMixinType) {
      try {
        const res = await fetchSubscriptionStatus(this.columnId);
        if (res.status_code !== 0 || !res.data) {
          return;
        }

        this.isSubscribed = res.data.subscription_status === SUBSCRIBE_TYPE.SUBSCRIBE;
      } catch (error) {
        //
      }
    },

    // 切换订阅状态
    async toggleSubscription(this: SubscribeMixinType) {
      if (this.loading) {
        return;
      }
      statClick('ths_mob_yanbao_pagetop_clickSubcribe');
      this.loading = true;
      this.$emit('toggleSubscribe', { status: this.isSubscribed });

      const isCancelSubscribe = this.isSubscribed;

      // 1. 更新订阅状态
      try {
        const res = await updateSubscriptionStatus(
          this.columnId,
          isCancelSubscribe ? SUBSCRIBE_TYPE.UNSUBSCRIBE : SUBSCRIBE_TYPE.SUBSCRIBE
        );

        if (res.status_code !== 0 || !res.data) {
          throw res.status_msg;
        }

        this.isSubscribed = !isCancelSubscribe;
        this.isPushOpen = !!res.data.push_status;
      } catch (error) {
        Toast(isCancelSubscribe ? UN_SUBSCRIBE_FAILED_MSG : SUBSCRIBE_FAILED_MSG);
        return;
      } finally {
        this.loading = false;
      }

      // 2. 取消订阅则需要关闭已打开的push，无其他后续
      if (isCancelSubscribe) {
        Toast(CANCEL_SUBSCRIBE_SUC_MSG);
        return;
      }

      // 3. 检查推送状态是否打开，且获取当前厂商是否需要二次确认
      this.isNeedConfirmPush = needConfirmPush(this.needConfirmPushDevice);
      this.isSystemPushOpen = await getNotificationStatus();

      // 4. 系统通知没开 或者 推送没开且需要二次确认的厂商，展示弹窗
      if (!this.isSystemPushOpen || (!this.isPushOpen && this.isNeedConfirmPush)) {
        this.showConfirmPushDialog();
        return;
      }

      // 5. 推送没开，但是是无需二次确认的厂商，自动调用推送打开请求
      if (!this.isPushOpen && !this.isNeedConfirmPush) {
        await this.setPushSwitch('open');
      }

      // 6. 展示开启成功弹窗
      this.showSuccessDialog();
    },

    // 打开功能推送开关
    async setPushSwitch(this: SubscribeMixinType, action: 'open' | 'close' = 'open') {
      try {
        await setPushSwitch(this.columnId, action);
      } catch (error) {
        //
      }
    },

    // 显示推送确认弹窗
    showConfirmPushDialog(this: SubscribeMixinType) {
      Dialog.confirm({
        title: '推送通知',
        message: '您还需要打开推送通知，才能收到您订阅的消息。',
        confirmButtonText: '打开',
        cancelButtonText: '取消',
      })
        .then(() => {
          if (!this.isPushOpen) {
            this.setPushSwitch('open');
          }

          if (!this.isSystemPushOpen) {
            jumpNotificationSetting();
          }
        })
        .catch(() => {});
    },

    // 显示订阅成功提示
    showSuccessDialog(this: SubscribeMixinType) {
      Dialog({
        title: '订阅成功',
        message: `您会收到${this.businessName}的推送消息。您可以在“个人中心-消息设置”中管理。`,
        confirmButtonText: '我知道了',
        // @ts-ignore
        isMinor: true,
      });
    },
  },
} as const;

export default subscribeMixin;
